import { wirePathToImageDisplaySrc } from './assetImageResolve';
import {
  contactHintsFromAuthBody,
  contactHintsFromJwt,
  contactHintsFromLoginIdentifier,
  getStoredProfileEmail,
  getStoredProfilePhone,
  mergeContactHints,
  persistProfileContactHints,
} from './profileContactStorage';
import { getStoredProfileDetailsHints, persistProfileDetailsHints } from './profileDetailsStorage';
import { localBookingProfileWireSnapshot } from './profileBookingCompleteStorage';
import {
  isPhoneLiteRegisterSyntheticEmail,
  isLiteRegisterPlaceholderBirthDate,
  isLiteRegisterPlaceholderLastName,
  isLiteRegisterPlaceholderPhoneNumber,
  nullifyLiteRegisterPlaceholderBirthDate,
  nullifyLiteRegisterPlaceholderFirstName,
  nullifyLiteRegisterPlaceholderLastName,
  nullifyLiteRegisterPlaceholderPhoneNumber,
  profileRecordUsesLiteRegisterPlaceholders,
} from './profileLitePlaceholders';
import { formatIsraeliPhoneDisplay, formatPhoneForDisplay } from './phoneE164';
import { ownerLogoFromRecord } from './ownerLogoFromRecord';
import {
  ASSET_OWNER_PAYOUT_FIELD_KEYS,
  flattenAssetOwnerPayoutPayload,
} from './assetOwnerPayoutBank';
import {
  extractProfileBusinessId,
  extractProfileBusinessName,
  extractProfilePayoutSummary,
  extractProfileSecondaryPhone,
  payoutDisplaySummaryFromWirePayload,
  type ProfilePayoutDisplaySummary,
} from './profileAssetOwnerDisplay';
import {
  extractBusinessClientRegistrationIdentifier,
} from './businessClientRegistrationIdentifier';
import { getStoredBusinessClientWorkerIdentifier, persistBusinessClientWorkerIdentifier } from './businessClientIdentifierStorage';
import {
  fallbackWorkerDisplayName,
  isBusinessWorkerProfileRecord,
  isOwnerLogoImageUrl,
  meaningfulWorkerField,
  shouldUseWorkerPersonalNameFields,
  workerRowEchoesEmployerContact,
} from './profileWorkerIdentity';
import {
  getStoredProfilePayoutWireHints,
  mergeProfilePayoutSummaries,
} from './profilePayoutStorage';
import { extractProfileVehicles, type ProfileVehicle } from './profileVehicles';
import { resolveProfileVehiclesForSummary } from './profileVehiclesStorage';
import {
  extractProfileUpdatedAtIso,
  resolveProfileLastUpdatedIso,
} from './profileLastUpdated';

export {
  contactHintsFromAuthBody,
  contactHintsFromJwt,
  contactHintsFromLoginIdentifier,
  getStoredProfileEmail,
  getStoredProfilePhone,
  mergeContactHints,
  persistProfileContactHints,
} from './profileContactStorage';

export type { ProfileVehicle } from './profileVehicles';

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function pickString(...values: unknown[]): string | null {
  for (const v of values) {
    if (typeof v === 'string' && v.trim()) {
      return v.trim();
    }
  }
  return null;
}

function pickFiniteNumber(...values: unknown[]): number | null {
  for (const v of values) {
    if (typeof v === 'number' && Number.isFinite(v)) {
      return v;
    }
    if (typeof v === 'string' && v.trim()) {
      const parsed = Number(v.trim());
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
  }
  return null;
}

const PROFILE_IMAGE_KEYS = [
  'profile_image',
  'profileImage',
  'avatar',
  'avatar_url',
  'avatarUrl',
  'image',
  'image_url',
  'imageUrl',
  'photo',
  'photo_url',
  'picture',
] as const;

export function profileImageFromRecord(record: Record<string, unknown>): string | null {
  for (const key of PROFILE_IMAGE_KEYS) {
    const v = record[key];
    if (typeof v === 'string' && v.trim()) {
      return v.trim();
    }
  }
  return null;
}

const PROFILE_RECORD_WRAPPER_KEYS = [
  'data',
  'user',
  'profile',
  'client',
  'result',
  'payload',
  'private_client',
  'private_clients',
  'business_client',
  'businessClient',
  'worker',
  'asset_owner',
  'wallet',
] as const;

const PROFILE_FIELD_HINT_KEYS = [
  'email',
  'phone_number',
  'phone',
  'mobile',
  'first_name',
  'last_name',
  'contact_first_name',
  'contact_last_name',
  'birth_date',
  'occupation',
  'business_name',
  'business_id',
] as const;

function recordHasProfileFields(record: Record<string, unknown>): boolean {
  return PROFILE_FIELD_HINT_KEYS.some((key) => {
    const value = record[key];
    return typeof value === 'string' && value.trim().length > 0;
  });
}

/** Fill top-level booking/display fields from nested profile envelopes when the API splits them. */
export function enrichProfileRecordFromNestedWrappers(
  record: Record<string, unknown>,
): Record<string, unknown> {
  const merged: Record<string, unknown> = { ...record };

  const nestedSources: Record<string, unknown>[] = [];
  for (const key of PROFILE_RECORD_WRAPPER_KEYS) {
    const nested = record[key];
    if (isPlainObject(nested)) {
      nestedSources.push(nested);
    }
  }
  if (nestedSources.length === 0) {
    return merged;
  }

  const fillIfMissing = (
    targetKey: string,
    read: (source: Record<string, unknown>) => string | null,
  ) => {
    if (pickString(merged[targetKey])) {
      return;
    }
    for (const source of nestedSources) {
      const value = read(source);
      if (value) {
        merged[targetKey] = value;
        return;
      }
    }
  };

  fillIfMissing('first_name', (source) => {
    if (isBusinessWorkerProfileRecord(merged)) {
      return pickString(source.first_name, source.firstName);
    }
    return pickString(
      source.contact_first_name,
      source.contactFirstName,
      source.first_name,
      source.firstName,
    );
  });
  fillIfMissing('last_name', (source) => {
    if (isBusinessWorkerProfileRecord(merged)) {
      return pickString(source.last_name, source.lastName);
    }
    return pickString(
      source.contact_last_name,
      source.contactLastName,
      source.last_name,
      source.lastName,
    );
  });
  if (!isBusinessWorkerProfileRecord(merged)) {
    fillIfMissing('contact_first_name', (source) =>
      pickString(source.contact_first_name, source.contactFirstName, source.first_name, source.firstName),
    );
    fillIfMissing('contact_last_name', (source) =>
      pickString(source.contact_last_name, source.contactLastName, source.last_name, source.lastName),
    );
  }
  fillIfMissing('email', (source) => pickString(source.email, source.Email));
  fillIfMissing('phone_number', (source) =>
    pickString(source.phone_number, source.phoneNumber, source.phone, source.mobile, source.cell_phone),
  );
  fillIfMissing('birth_date', (source) =>
    pickString(source.birth_date, source.date_of_birth, source.dob, source.birthDate),
  );
  fillIfMissing('occupation', (source) =>
    pickString(source.occupation, source.job_title, source.jobTitle, source.profession),
  );
  fillIfMissing('business_name', (source) => pickString(source.business_name, source.businessName));
  fillIfMissing('business_id', (source) => pickString(source.business_id, source.businessId));
  fillIfMissing('logo_url', (source) =>
    pickString(source.logo_url, source.logoUrl, source.brand_logo, source.business_logo),
  );
  for (const creditsKey of [
    'credits',
    'available_credits',
    'credit_balance',
    'credits_balance',
    'remaining_credits',
    'remainingCredits',
    'employer_credits',
    'employer_credit_balance',
    'credits_per_employee',
    'creditsPerEmployee',
  ] as const) {
    if (pickFiniteNumber(merged[creditsKey]) !== null) {
      continue;
    }
    for (const source of nestedSources) {
      const value = pickFiniteNumber(source[creditsKey]);
      if (value !== null) {
        merged[creditsKey] = value;
        break;
      }
    }
  }
  fillIfMissing('phone_number_2', (source) =>
    pickString(source.phone_number_2, source.phoneNumber2, source.secondary_phone_number),
  );

  if (!isPlainObject(merged.bank_account)) {
    for (const source of nestedSources) {
      if (isPlainObject(source.bank_account)) {
        merged.bank_account = source.bank_account;
        break;
      }
    }
  }

  const bankFlat = flattenAssetOwnerPayoutPayload(merged);
  for (const key of ASSET_OWNER_PAYOUT_FIELD_KEYS) {
    const current = merged[key];
    const next = bankFlat[key];
    const currentEmpty =
      current === null ||
      current === undefined ||
      (typeof current === 'string' && !current.trim());
    const nextPresent =
      next !== null &&
      next !== undefined &&
      (typeof next !== 'string' || next.trim().length > 0);
    if (currentEmpty && nextPresent) {
      merged[key] = next;
    }
  }
  if (
    !pickString(typeof merged.account_number === 'string' ? merged.account_number : null) &&
    pickString(typeof bankFlat.account_number_masked === 'string' ? bankFlat.account_number_masked : null)
  ) {
    merged.account_number_masked = bankFlat.account_number_masked;
  }

  for (const source of nestedSources) {
    const flat = flattenAssetOwnerPayoutPayload(source);
    for (const key of ASSET_OWNER_PAYOUT_FIELD_KEYS) {
      const current = merged[key];
      const next = flat[key];
      const currentEmpty =
        current === null ||
        current === undefined ||
        (typeof current === 'string' && !current.trim());
      const nextPresent =
        next !== null &&
        next !== undefined &&
        (typeof next !== 'string' || next.trim().length > 0);
      if (currentEmpty && nextPresent) {
        merged[key] = next;
      }
    }
  }

  if (shouldUseWorkerPersonalNameFields(merged)) {
    if (workerRowEchoesEmployerContact(merged)) {
      delete merged.first_name;
      delete merged.firstName;
      delete merged.last_name;
      delete merged.lastName;
    }
    delete merged.contact_first_name;
    delete merged.contactFirstName;
    delete merged.contact_last_name;
    delete merged.contactLastName;
  }

  return merged;
}

/** Unwrap typical GET /profile envelopes so email/phone live on the record we read. */
export function resolveProfileRecord(data: unknown): Record<string, unknown> | null {
  if (!isPlainObject(data)) {
    return null;
  }
  if (recordHasProfileFields(data)) {
    return enrichProfileRecordFromNestedWrappers(data);
  }
  for (const key of PROFILE_RECORD_WRAPPER_KEYS) {
    const nested = data[key];
    if (isPlainObject(nested) && recordHasProfileFields(nested)) {
      return enrichProfileRecordFromNestedWrappers(nested);
    }
  }
  for (const key of PROFILE_RECORD_WRAPPER_KEYS) {
    const nested = data[key];
    if (isPlainObject(nested)) {
      return enrichProfileRecordFromNestedWrappers(nested);
    }
  }
  return enrichProfileRecordFromNestedWrappers(data);
}

function extractEmailFromRecord(record: Record<string, unknown> | null): string | null {
  if (!record) {
    return null;
  }
  return pickString(record.email, record.Email);
}

function extractPhoneFromRecord(record: Record<string, unknown> | null): string | null {
  if (!record) {
    return null;
  }
  return pickString(record.phone_number, record.phoneNumber, record.phone, record.mobile, record.cell_phone);
}

export type { ProfilePayoutDisplaySummary } from './profileAssetOwnerDisplay';

export type ProfileDisplaySummary = {
  displayName: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  phoneSecondary: string | null;
  dateOfBirth: string | null;
  occupation: string | null;
  businessName: string | null;
  businessId: string | null;
  /** 8-digit code for business workers to self-register (business client accounts). */
  workerRegistrationCode: string | null;
  avatarSrc: string | null;
  /** Asset owner business logo from GET /profile (`logo_url` or `logo`). */
  ownerLogoSrc: string | null;
  /** Bank payout details for asset owner profile payment section. */
  payout: ProfilePayoutDisplaySummary | null;
  /** Gym health declaration on file (`medical_questionnaire_completed` on GET /profile). */
  medicalQuestionnaireCompleted: boolean;
  /** Employer-allocated booking credits for business workers (GET /profile). */
  availableCredits: number | null;
  /** Optional cars on the profile (max 3). */
  vehicles: ProfileVehicle[];
  /** Server or local marker for the last profile change (for support / sync checks). */
  updatedAtIso: string | null;
};

export function extractProfileFirstName(record: Record<string, unknown> | null): string | null {
  if (!record) {
    return null;
  }
  if (shouldUseWorkerPersonalNameFields(record)) {
    if (workerRowEchoesEmployerContact(record)) {
      return null;
    }
    return meaningfulWorkerField(pickString(record.first_name, record.firstName));
  }
  return pickString(
    record.contact_first_name,
    record.contactFirstName,
    record.first_name,
    record.firstName,
  );
}

export function extractProfileLastName(record: Record<string, unknown> | null): string | null {
  if (!record) {
    return null;
  }
  if (shouldUseWorkerPersonalNameFields(record)) {
    if (workerRowEchoesEmployerContact(record)) {
      return null;
    }
    return meaningfulWorkerField(pickString(record.last_name, record.lastName));
  }
  return pickString(record.contact_last_name, record.contactLastName, record.last_name, record.lastName);
}

/** Employer default allocation per worker (`credits_per_employee` on business client / worker profile). */
export function extractProfileEmployerCreditsPerEmployee(
  record: Record<string, unknown> | null,
): number | null {
  if (!record) {
    return null;
  }
  return pickFiniteNumber(
    record.credits_per_employee,
    record.creditsPerEmployee,
    record.employee_credits,
    record.default_credits,
    record.default_credit_amount,
  );
}

/** True when the profile payload includes an explicit wallet balance field. */
export function profileRecordHasRemainingCreditsField(
  record: Record<string, unknown> | null,
): boolean {
  if (!record) {
    return false;
  }
  if (
    Object.prototype.hasOwnProperty.call(record, 'remaining_credits') ||
    Object.prototype.hasOwnProperty.call(record, 'remainingCredits')
  ) {
    return true;
  }
  for (const key of ['wallet', 'private_client', 'private_clients', 'data', 'profile', 'user'] as const) {
    const nested = record[key];
    if (
      nested !== null &&
      typeof nested === 'object' &&
      !Array.isArray(nested) &&
      (Object.prototype.hasOwnProperty.call(nested, 'remaining_credits') ||
        Object.prototype.hasOwnProperty.call(nested, 'remainingCredits'))
    ) {
      return true;
    }
  }
  return false;
}

/** Remaining wallet / employer credits on a profile (`GET /profile` → `remaining_credits`). */
export function extractProfileAvailableCredits(record: Record<string, unknown> | null): number | null {
  if (!record) {
    return null;
  }
  // Prefer the private-client / worker wallet column (including 0).
  const walletBalance = pickFiniteNumber(record.remaining_credits, record.remainingCredits);
  if (walletBalance !== null) {
    return walletBalance;
  }
  for (const key of ['wallet', 'private_client', 'private_clients'] as const) {
    const nested = record[key];
    if (nested !== null && typeof nested === 'object' && !Array.isArray(nested)) {
      const nestedWallet = pickFiniteNumber(
        (nested as Record<string, unknown>).remaining_credits,
        (nested as Record<string, unknown>).remainingCredits,
      );
      if (nestedWallet !== null) {
        return nestedWallet;
      }
    }
  }
  const remainingBalance = pickFiniteNumber(
    record.available_credits,
    record.credit_balance,
    record.credits_balance,
    record.employer_credits,
    record.employer_credit_balance,
    // `credits` last — often a different/zero field that was wiping wallet reads.
    record.credits,
  );
  if (remainingBalance !== null) {
    return remainingBalance;
  }
  return extractProfileEmployerCreditsPerEmployee(record);
}

export function extractProfileDateOfBirth(record: Record<string, unknown> | null): string | null {
  return record ? pickString(record.birth_date, record.date_of_birth, record.dob, record.birthDate) : null;
}

export function extractProfileOccupation(record: Record<string, unknown> | null): string | null {
  return record
    ? pickString(record.occupation, record.job_title, record.jobTitle, record.profession)
    : null;
}

/** `GET /profile` — private client (role 1) and business worker (role 3). */
export function extractMedicalQuestionnaireCompleted(record: Record<string, unknown> | null): boolean {
  if (!record) {
    return false;
  }
  for (const key of ['medical_questionnaire_completed', 'medicalQuestionnaireCompleted'] as const) {
    const value = record[key];
    if (typeof value === 'boolean') {
      return value;
    }
  }
  return false;
}

export function formatProfileDateOfBirthDisplay(raw: string | null | undefined): string | null {
  const value = raw?.trim();
  if (!value || isLiteRegisterPlaceholderBirthDate(value)) {
    return null;
  }
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    const date = new Date(Number(year), Number(month) - 1, Number(day));
    if (!Number.isNaN(date.getTime())) {
      return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    }
  }
  return value;
}

/** Split session display name (e.g. "Private Client") when the API omits first/last fields. */
export function splitFallbackPersonName(name: string | null | undefined): {
  firstName: string | null;
  lastName: string | null;
} {
  const trimmed = name?.trim();
  if (!trimmed) {
    return { firstName: null, lastName: null };
  }
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return { firstName: null, lastName: null };
  }
  if (parts.length === 1) {
    return { firstName: parts[0], lastName: null };
  }
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
}

function shouldIgnoreStoredWorkerPersonName(
  record: Record<string, unknown> | null,
  firstName: string | null | undefined,
  lastName: string | null | undefined,
): boolean {
  if (!record || !firstName?.trim() || !lastName?.trim()) {
    return false;
  }
  if (!shouldUseWorkerPersonalNameFields(record)) {
    return false;
  }
  return workerRowEchoesEmployerContact({
    ...record,
    first_name: firstName.trim(),
    last_name: lastName.trim(),
  });
}

export function extractProfileDisplaySummary(
  data: unknown,
  fallbackName: string | null,
  fallbackLoginId: string | null,
  accessToken?: string | null,
): ProfileDisplaySummary {
  const accountId = fallbackLoginId;
  const record = resolveProfileRecord(data);
  const storedDetails = getStoredProfileDetailsHints(accountId);
  const localBooking = localBookingProfileWireSnapshot(accountId);
  const nameFallback = splitFallbackPersonName(fallbackName);
  const apiFirstName = extractProfileFirstName(record);
  const apiLastName = extractProfileLastName(record);
  const apiOccupation = extractProfileOccupation(record);
  const apiDateOfBirth = extractProfileDateOfBirth(record);
  const storedFirstName = shouldIgnoreStoredWorkerPersonName(
    record,
    storedDetails.firstName,
    storedDetails.lastName,
  )
    ? null
    : storedDetails.firstName;
  const storedLastName = shouldIgnoreStoredWorkerPersonName(
    record,
    storedDetails.firstName,
    storedDetails.lastName,
  )
    ? null
    : storedDetails.lastName;
  const firstName =
    apiFirstName ??
    storedFirstName ??
    (localBooking?.first_name ? pickString(localBooking.first_name) : null) ??
    nameFallback.firstName;
  const lastName =
    apiLastName ??
    storedLastName ??
    (localBooking?.last_name ? pickString(localBooking.last_name) : null) ??
    nameFallback.lastName;

  const contactFallback = mergeContactHints(
    { email: extractEmailFromRecord(record), phone: extractPhoneFromRecord(record) },
    contactHintsFromAuthBody(data),
    { email: getStoredProfileEmail(accountId), phone: getStoredProfilePhone(accountId) },
    contactHintsFromLoginIdentifier(fallbackLoginId),
    contactHintsFromJwt(accessToken ?? null),
    localBooking
      ? {
          email: localBooking.email ? pickString(localBooking.email) : null,
          phone: localBooking.phone_number ? pickString(localBooking.phone_number) : null,
        }
      : { email: null, phone: null },
  );

  const email = contactFallback.email;
  const phone = contactFallback.phone;

  const dateOfBirth =
    apiDateOfBirth ||
    storedDetails.birthDate ||
    (localBooking?.birth_date ? pickString(localBooking.birth_date) : null);
  const occupation =
    apiOccupation ||
    storedDetails.occupation ||
    (localBooking?.occupation ? pickString(localBooking.occupation) : null);

  const emailForPlaceholders = email ?? fallbackLoginId;
  // Only trust a locally saved DOB if it is not the auto-register placeholder.
  const trustedBirthDate =
    Boolean(storedDetails.birthDate?.trim()) &&
    !isLiteRegisterPlaceholderBirthDate(storedDetails.birthDate);

  const sanitizedFirstName = nullifyLiteRegisterPlaceholderFirstName(firstName, emailForPlaceholders, {
    lastName,
    birthDate: dateOfBirth,
  });
  const sanitizedLastName = storedDetails.lastName?.trim()
    ? lastName
    : nullifyLiteRegisterPlaceholderLastName(lastName);
  const sanitizedBirthDate = trustedBirthDate
    ? dateOfBirth
    : nullifyLiteRegisterPlaceholderBirthDate(dateOfBirth, lastName);

  if (isLiteRegisterPlaceholderBirthDate(storedDetails.birthDate)) {
    persistProfileDetailsHints({ birthDate: null }, accountId);
  }

  const apiLooksLite = profileRecordUsesLiteRegisterPlaceholders(record, emailForPlaceholders);
  const phoneLiteAccount = isPhoneLiteRegisterSyntheticEmail(email);
  const loginPhoneHint = contactHintsFromLoginIdentifier(fallbackLoginId).phone;
  let sanitizedPhone = phone;
  if (apiLooksLite && !localBooking?.phone_number?.trim()) {
    sanitizedPhone = phoneLiteAccount ? loginPhoneHint ?? phone : null;
  }
  if (!localBooking?.phone_number?.trim()) {
    sanitizedPhone = nullifyLiteRegisterPlaceholderPhoneNumber(sanitizedPhone);
  }

  const sanitizedEmail = phoneLiteAccount ? null : email;

  if (sanitizedEmail || sanitizedPhone || isLiteRegisterPlaceholderPhoneNumber(phone)) {
    persistProfileContactHints(sanitizedEmail, sanitizedPhone, accountId);
  }

  const joined = [sanitizedFirstName, sanitizedLastName].filter(Boolean).join(' ').trim();
  const phoneDisplayLabel =
    sanitizedPhone && phoneLiteAccount ? formatPhoneForDisplay(sanitizedPhone) : null;
  const emailForDisplay = sanitizedEmail ?? email ?? fallbackLoginId;
  const workerNameFallback =
    record && shouldUseWorkerPersonalNameFields(record) && !joined
      ? fallbackWorkerDisplayName(emailForDisplay)
      : null;
  const displayName =
    (record && !workerNameFallback
      ? pickString(record.name, record.display_name, record.displayName)
      : null) ??
    (joined || null) ??
    workerNameFallback ??
    phoneDisplayLabel ??
    (fallbackName?.trim() || fallbackLoginId?.trim() || '');

  let avatarRaw = record ? profileImageFromRecord(record) : null;
  if (avatarRaw && isOwnerLogoImageUrl(avatarRaw)) {
    avatarRaw = null;
  }
  const avatarSrc = avatarRaw ? wirePathToImageDisplaySrc(avatarRaw) ?? avatarRaw : null;
  const ownerLogoRaw = record ? ownerLogoFromRecord(record) : null;
  const ownerLogoSrc = ownerLogoRaw ? wirePathToImageDisplaySrc(ownerLogoRaw) ?? ownerLogoRaw : null;

  const formatPhoneLabel = (value: string | null): string | null => {
    if (!value) {
      return null;
    }
    return formatIsraeliPhoneDisplay(value);
  };

  const storedPayoutWire = getStoredProfilePayoutWireHints(accountId);
  const apiPayout = extractProfilePayoutSummary(record);
  const storedPayout = storedPayoutWire
    ? payoutDisplaySummaryFromWirePayload(storedPayoutWire)
    : null;
  const payout = mergeProfilePayoutSummaries(apiPayout, storedPayout);

  const apiWorkerCode = extractBusinessClientRegistrationIdentifier(record ?? data);
  const storedWorkerCode = getStoredBusinessClientWorkerIdentifier();
  const workerRegistrationCode = apiWorkerCode ?? storedWorkerCode;
  if (apiWorkerCode) {
    persistBusinessClientWorkerIdentifier(apiWorkerCode);
  }

  return {
    displayName,
    firstName: sanitizedFirstName,
    lastName: sanitizedLastName,
    email: sanitizedEmail,
    phone: formatPhoneLabel(sanitizedPhone),
    phoneSecondary: formatPhoneLabel(extractProfileSecondaryPhone(record)),
    dateOfBirth: sanitizedBirthDate,
    occupation,
    businessName: extractProfileBusinessName(record),
    businessId: extractProfileBusinessId(record),
    workerRegistrationCode,
    avatarSrc,
    ownerLogoSrc,
    payout,
    medicalQuestionnaireCompleted: extractMedicalQuestionnaireCompleted(record),
    availableCredits: extractProfileAvailableCredits(record),
    vehicles: resolveProfileVehiclesForSummary(
      record,
      extractProfileVehicles(record),
      accountId,
    ),
    updatedAtIso: resolveProfileLastUpdatedIso(extractProfileUpdatedAtIso(data), accountId),
  };
}
